import React from 'react'
import { ModalDialogContainer } from '@common/modal'
import {
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  LoadingButton,
  MenuItem,
  TextField,
} from '@common/material'
import { useModal } from '@common/GlobalModal'
import { ScheduledInspection } from '@core/entities/register/RegisterInspection'
import {
  useCreateRegisterItemScheduledInspection,
  useUpdateRegisterItemScheduledInspection,
} from '@core/react-query/features/registers/registers/register/items/item'
import { localizedStrings } from '@core/strings'
import { useToast } from '@core/toast'
import { Flex, Text } from '@fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import moment from 'moment'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { DatePicker } from '@common/material/DatePicker'
import { ProjectUserPicker } from '@elements'
import {
  SCHEDULE_INSPECTION_FREQUENCY_VALUES,
  type ScheduleInspectionFormValuesType,
} from '@core/entities/register/RegisterInspection/types'
import { ProjectUser } from '@core/entities/user'

type ComponentProps = {
  checklistTemplates: Array<{
    id: number
    name: string
  }>
  projectId?: number
  registerId: number
  registerItemId: number
  registerPropertyId: string
  scheduledInspection?: ScheduledInspection
}

export const RegisterScheduleInspectionsConfigForm: React.FC<
  ComponentProps
> = ({
  checklistTemplates,
  projectId,
  registerId,
  registerItemId,
  registerPropertyId,
  scheduledInspection,
}) => {
  const { hideModal } = useModal()
  const toast = useToast()
  const createScheduledInspection = useCreateRegisterItemScheduledInspection()
  const updateScheduledInspection = useUpdateRegisterItemScheduledInspection()

  const isEditingAnExistingSchedule = !!scheduledInspection

  const defaultValues: ScheduleInspectionFormValuesType = {
    // If there is only one template, preselect it. Otherwise, leave field empty for selection.
    inspectionId:
      checklistTemplates.length === 1 ? checklistTemplates[0].id : undefined,
    frequency: 'ONCE',
    dueDate: null,
    firstDueDate: null,
    customIntervalDays: 60,
    excludeWeekends: true,
    assigneeUserId: undefined,
  }

  const form = useForm<ScheduleInspectionFormValuesType>({
    defaultValues: scheduledInspection?.toFormValues() ?? defaultValues,
  })

  const { control, handleSubmit, setValue } = form
  const frequency = useWatch({ control, name: 'frequency' })
  const excludeWeekends = useWatch({ control, name: 'excludeWeekends' })
  const customIntervalDays = useWatch({ control, name: 'customIntervalDays' })

  const onSubmit = async (data: ScheduleInspectionFormValuesType) => {
    const payload = ScheduledInspection.getScheduleInspectionUpdatePayload(data)

    const createPayload = {
      ...payload,
      registerTemplateInspectionId: registerPropertyId,
    }

    if (scheduledInspection) {
      updateScheduledInspection.mutate(
        {
          registerId,
          registerItemId,
          scheduledInspectionId: scheduledInspection.id,
          data: payload,
        },
        {
          onSuccess: () => {
            toast.success(localizedStrings.scheduleUpdated)
            hideModal()
          },
          onError: (e) => toast.error(e.message),
        },
      )
    } else {
      createScheduledInspection.mutate(
        {
          registerId,
          registerItemId,
          data: createPayload,
        },
        {
          onSuccess: () => {
            toast.success(localizedStrings.scheduleCreated)
            hideModal()
          },
          onError: (e) => toast.error(e.message),
        },
      )
    }
  }

  const toggleExcludeWeekends = () => {
    setValue('excludeWeekends', !excludeWeekends, { shouldDirty: true })
  }

  const scheduleDateFieldName =
    frequency === 'ONCE' ? 'dueDate' : 'firstDueDate'

  const scheduleDateHelperText = (hasError: boolean, message?: string) => {
    if (hasError && message) return message
    if (frequency === 'ONCE') {
      return localizedStrings.dueDateHelperOnce
    }
    if (frequency === 'DAILY') {
      return localizedStrings.dueDateHelperDaily
    }
    return localizedStrings.dueDateHelperRecurring
  }

  const getFrequencyHelperText = (): string => {
    switch (frequency) {
      case 'ONCE':
        return localizedStrings.runsOnceOnly
      case 'DAILY':
        return localizedStrings.runsEverydayWeekends(excludeWeekends)
      case 'WEEKLY':
        return localizedStrings.repeatsEvery7Days
      case 'MONTHLY':
        return localizedStrings.runsEvery30DaysWeekends(excludeWeekends)
      case 'CUSTOM':
        return localizedStrings.runsCustomIntervalWeekends(
          customIntervalDays,
          excludeWeekends,
        )
      default:
        return ''
    }
  }

  const frequencyHelperText = getFrequencyHelperText()
  const showChangeLink =
    frequency === 'DAILY' || frequency === 'MONTHLY' || frequency === 'CUSTOM'

  return (
    <ModalDialogContainer PaperProps={{ sx: { width: 500 } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20 }}
        >
          {isEditingAnExistingSchedule
            ? localizedStrings.editInspection
            : localizedStrings.setupAnInspection}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Controller
            name='inspectionId'
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label={localizedStrings.inspectionChecklist}
                fullWidth
                required
                sx={{ mt: 1 }}
                size='medium'
                error={!!fieldState.error}
              >
                {checklistTemplates?.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Flex flexDirection='column' gap={0.5}>
            <Text variant='body1' fontWeight={500}>
              {localizedStrings.scheduleAndDueDate}
            </Text>
            <Text variant='body2' color='text.secondary'>
              {localizedStrings.chooseWhenAndHowOftenInspection}
            </Text>
          </Flex>

          <Controller
            name='frequency'
            control={control}
            render={({ field }) => (
              <Flex flexDirection='column' gap={1}>
                <Flex
                  flexDirection='row'
                  flexWrap='nowrap'
                  gap={1}
                  alignItems='center'
                  sx={{
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 6 },
                  }}
                >
                  {SCHEDULE_INSPECTION_FREQUENCY_VALUES.map((opt) => {
                    const selected = field.value === opt
                    return (
                      <Chip
                        key={opt}
                        label={ScheduledInspection.frequencyOptionLabel(opt)}
                        clickable
                        data-test={`schedule-frequency-${opt}`}
                        onClick={() => field.onChange(opt)}
                        sx={{
                          borderRadius: '4px',
                          minWidth: '80px',
                          bgcolor: selected ? 'primary.main' : 'default.main',
                          color: selected
                            ? 'primary.contrastText'
                            : 'text.primary',
                          '&:hover': {
                            bgcolor: selected
                              ? 'primary.dark'
                              : 'default.hover',
                          },
                        }}
                      />
                    )
                  })}
                </Flex>
                {frequencyHelperText != null && (
                  <Text
                    variant='caption'
                    color='text.secondary'
                    component={showChangeLink ? 'span' : 'p'}
                  >
                    {frequencyHelperText}
                    {showChangeLink && (
                      <>
                        {'. '}
                        <Link
                          component='button'
                          type='button'
                          underline='hover'
                          onClick={(e) => {
                            e.preventDefault()
                            toggleExcludeWeekends()
                          }}
                          sx={{ verticalAlign: 'baseline' }}
                        >
                          {localizedStrings.change}
                        </Link>
                      </>
                    )}
                  </Text>
                )}
              </Flex>
            )}
          />

          <Flex flexDirection='column' gap={2}>
            {frequency === 'CUSTOM' && (
              <>
                <Controller
                  name='customIntervalDays'
                  control={control}
                  rules={{
                    required: true,
                    min: {
                      value: 1,
                      message: localizedStrings.mustBeAtLeast1Day,
                    },
                    max: {
                      value: 365,
                      message: localizedStrings.mustBeAtMost365Days,
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      type='number'
                      label={localizedStrings.customIntervalDays}
                      fullWidth
                      size='small'
                      inputProps={{ min: 1 }}
                      value={field.value}
                      error={!!fieldState.error}
                      required
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                    />
                  )}
                />
              </>
            )}

            <Controller
              key={scheduleDateFieldName}
              name={scheduleDateFieldName}
              control={control}
              rules={{
                validate: (value: string | null) => {
                  if (frequency === 'ONCE') {
                    if (!value) return localizedStrings.dueDateRequired
                    return (
                      moment(value).isSameOrAfter(moment(), 'minute') ||
                      localizedStrings.dueDateMustBeTodayOrInTheFuture
                    )
                  }
                  if (!value) return localizedStrings.firstDueDateRequired
                  return (
                    moment(value).isSameOrAfter(moment(), 'minute') ||
                    localizedStrings.firstDueDateMustBeTodayOrInTheFuture
                  )
                },
              }}
              render={({ field, fieldState }) => (
                <DatePicker
                  withTime
                  disablePast
                  dataTest='schedule-inspection-due-date'
                  label={
                    frequency === 'ONCE'
                      ? localizedStrings.dueDate
                      : localizedStrings.firstDueDate
                  }
                  sx={{ width: '100%' }}
                  value={field.value ? moment(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? moment(date).toISOString() : null)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'medium',
                      error: !!fieldState.error,
                      helperText: scheduleDateHelperText(
                        !!fieldState.error,
                        fieldState.error?.message,
                      ),
                    },
                  }}
                />
              )}
            />
          </Flex>

          <Flex flexDirection='column' mt={3}>
            {projectId ? (
              <Controller
                name='assigneeUserId'
                control={control}
                rules={{
                  validate: (value) =>
                    value != null || localizedStrings.pleaseSelectAssignee,
                }}
                render={({ field, fieldState }) => (
                  <ProjectUserPicker
                    projectId={projectId}
                    allowSelf
                    initialUsers={
                      scheduledInspection
                        ? [ProjectUser.new(scheduledInspection.assignedTo)]
                        : undefined
                    }
                    label={localizedStrings.assignee}
                    size='medium'
                    value={field.value}
                    onChange={(userId) =>
                      field.onChange(
                        typeof userId === 'number' ? userId : undefined,
                      )
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    required
                  />
                )}
              />
            ) : (
              <TextField
                label={localizedStrings.assignee}
                fullWidth
                disabled
                helperText={localizedStrings.assigneeRequiresProjectContext}
              />
            )}
          </Flex>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, flexDirection: 'column' }}>
          <LoadingButton
            type='submit'
            variant='contained'
            color='primary'
            fullWidth
            size='large'
            data-test='submit-scheduled-inspection'
            loading={
              createScheduledInspection.isLoading ||
              updateScheduledInspection.isLoading
            }
            disabled={!projectId}
          >
            {isEditingAnExistingSchedule
              ? localizedStrings.save
              : localizedStrings.createSchedule}
          </LoadingButton>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}
